/**
 * Atlas Logger
 * Minimal structured logging interface shared across all packages.
 *
 * Why here (types-js)?
 * - Every atlas-forms package depends on types-js.
 * - Putting this here avoids circular dependencies while making
 *   structured logging available everywhere.
 * - The default implementation delegates to console.* so packages
 *   work out of the box without any setup.
 * - Production apps replace the default with SafeLogger (from client-js)
 *   by calling setAtlasLogger() at application startup.
 */

/**
 * General-purpose structured logger interface.
 * Compatible with SafeLogger from @atlas-forms/client-js.
 */
export interface IAtlasLogger {
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  error(message: string, data?: unknown): void;
}

/**
 * Console-backed default implementation.
 * Used automatically until the app injects a production logger.
 */
const consoleAtlasLogger: IAtlasLogger = {
  debug: (msg, data) => (data !== undefined ? console.debug(msg, data) : console.debug(msg)),
  info:  (msg, data) => (data !== undefined ? console.info(msg, data)  : console.info(msg)),
  warn:  (msg, data) => (data !== undefined ? console.warn(msg, data)  : console.warn(msg)),
  error: (msg, data) => (data !== undefined ? console.error(msg, data) : console.error(msg)),
};

let _atlasLogger: IAtlasLogger = consoleAtlasLogger;

/**
 * Replace the global logger with a production implementation (e.g. SafeLogger).
 * Call this once at application startup before any atlas-forms code runs.
 *
 * @example
 * import { SafeLogger } from '@atlas-forms/client-js';
 * import { setAtlasLogger } from '@atlas-forms/types-js';
 * setAtlasLogger(SafeLogger);
 */
export function setAtlasLogger(logger: IAtlasLogger): void {
  _atlasLogger = logger;
}

/**
 * Get the active global logger.
 * All atlas-forms packages call this to obtain the current logger.
 */
export function getAtlasLogger(): IAtlasLogger {
  return _atlasLogger;
}
